import React from "react";
import { CircleAlert, CircleX, CircleCheckBig } from "lucide-react";
import { useSimulator } from "../Simulator/SimulatorStore";

function MetricBadge({ label, value, numeric }: { label: string; value: string; numeric?: number }) {
  const numericVal = numeric !== undefined ? numeric : (value.endsWith("%") ? parseInt(value.replace(/%$/, ""), 10) : undefined);
  const efficiencyClass = label === "Efficiency"
    ? numericVal !== undefined && numericVal >= 80
      ? "text-green-600"
      : numericVal !== undefined && numericVal < 60
        ? "text-red-600"
        : "text-yellow-600"
    : "";
  return (
    <div>
      <span className="text-gray-600 text-xs">{label}</span>
      <p className={`font-medium text-xs ${efficiencyClass}`}>{value}</p>
    </div>
  );
}

function SliderRow({ label, value, onChange, min = 0, max = 100 }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span>{label}</span><span>{value}%</span></div>
      <input
        min={min}
        max={max}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        type="range"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function StaffRow({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span>Staff</span><span>{value}</span></div>
      <input
        min={1}
        max={5}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        type="range"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function ProcessCard({ index, title }: { index: number; title: string }) {
  const { processes, updateProcessFactors, bottleneckIndex } = useSimulator();
  const p = processes[index];
  const staff = p.factors.staff;
  const experience = p.factors.experience;
  const automation = p.factors.automation;
  const maturity = p.factors.maturity;

  // Dynamic efficiency: proportion of raw demand handled productively (throughput vs demand) after rework penalty.
  // Efficiency = (Throughput / Demand) * (1 - Rework%) * 100, capped at 100.
  const efficiencyPct = Math.min(
    100,
    (p.derived.throughputPerDay / Math.max(1, p.derived.demandPerDay)) * (1 - p.derived.effectiveReworkPct / 100) * 100
  );
  const utilizationPct = Math.round(p.derived.utilizationPct);
  const queue = Math.round(p.derived.queueIncreasePerDay);
  const isBottleneck = index === bottleneckIndex;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
          {Math.round(efficiencyPct) >= 80 ? (
            <CircleCheckBig className="h-5 w-5 text-green-600" />
          ) : Math.round(efficiencyPct) < 60 ? (
            <CircleX className="h-5 w-5 text-red-600" />
          ) : (
            <CircleAlert className="h-5 w-5 text-yellow-600" />
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <MetricBadge label="Efficiency" value={`${Math.round(efficiencyPct)}%`} numeric={Math.round(efficiencyPct)} />
          <MetricBadge label="Utilization" value={`${utilizationPct}%`} numeric={utilizationPct} />
          <MetricBadge label="Queue" value={`${queue}`} numeric={queue} />
        </div>
        {isBottleneck && (
          <div className="text-red-600 text-xs font-medium mt-2">Bottleneck (capacity-limiting)</div>
        )}
      </div>
      <div className="p-4 space-y-3">
        <StaffRow value={staff} onChange={(v) => updateProcessFactors(index, { staff: v })} />
        <SliderRow label="Experience" min={20} value={experience} onChange={(v) => updateProcessFactors(index, { experience: v })} />
        <SliderRow label="Automation" value={automation} onChange={(v) => updateProcessFactors(index, { automation: v })} />
        <SliderRow label="Maturity" value={maturity} onChange={(v) => updateProcessFactors(index, { maturity: v })} />
      </div>
    </div>
  );
}

export default function ProcessPerformance() {
  return (
    <div className="w-100 flex-shrink-0">
      <div className="space-y-4">
        <ProcessCard index={0} title="Contact Management" />
        <ProcessCard index={1} title="Pipeline Management" />
        <ProcessCard index={2} title="Document Review" />
        <ProcessCard index={3} title="Quoting" />
        <ProcessCard index={4} title="Compliance" />
      </div>
    </div>
  );
}
